import { useState, type ComponentProps } from "react"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import Input from "@/v2-ui/input"
import { DATA_VIZ } from "@/v2-ui/color/dataViz.colors"
import Link from "@/v2-backoffice-client/components/link"
import PortfolioSectionHeader from "@/v2-backoffice-client/portfolio/PortfolioSectionHeader"
import UsageReportCard from "@/v2-backoffice-client/usage/report/UsageReportCard"
import UsageChurnRiskBadge from "@/v2-backoffice-client/usage/scope/UsageChurnRiskBadge"
import { formatCompactAmount } from "@/v2-backoffice-client/customer/billing/customerBilling.utils"
import { deriveIdleLicenseTargets } from "@/v2-backoffice-client/portfolio/portfolio.health.utils"
import {
  buildCustomerActivityLink,
  type PortfolioCustomerSummary,
} from "@/v2-backoffice-client/portfolio/portfolio.utils"

export type PortfolioIdleLicensesProps = ComponentProps<"section"> & {
  customers: PortfolioCustomerSummary[]
}

const PREVIEW_LIMIT = 8

// The activation hit list: accounts ranked by idle licenses. Idle seats are
// paid-for value the customer isn't getting — the cheapest churn defense is
// onboarding them before the renewal, so the biggest piles come first.
function PortfolioIdleLicenses(props: PortfolioIdleLicensesProps) {
  const { customers, className, ...sectionProps } = props

  const [showAll, setShowAll] = useState(false)
  const [search, setSearch] = useState("")

  const targets = deriveIdleLicenseTargets(customers)
  const totalIdle = targets.reduce((sum, target) => sum + target.idleSeats, 0)
  // Money paying for unused seats across the list — the sum a fleet-wide
  // right-sizing wave would take off the book (or activation would defend).
  const totalIdleValue = targets.reduce(
    (sum, target) => sum + (target.idleValue12M ?? 0),
    0,
  )
  const currency =
    targets.find((target) => target.customer.primaryCurrency != null)?.customer
      .primaryCurrency ?? null
  const searchTerm = search.trim().toLowerCase()
  const matching =
    searchTerm === ""
      ? targets
      : targets.filter((target) =>
          target.customer.name.toLowerCase().includes(searchTerm),
        )
  // A search looks across ALL accounts, not just the visible preview.
  const visible =
    showAll || searchTerm !== "" ? matching : matching.slice(0, PREVIEW_LIMIT)
  const hiddenCount = matching.length - visible.length

  return (
    <section
      className={cn("usage-report-block flex flex-col gap-4", className)}
      {...sectionProps}
    >
      <PortfolioSectionHeader
        eyebrow="Licenses"
        title="License utilization"
        lead="Accounts ranked by seats nobody has used in the last 28 days — the seats a renewal conversation has to defend."
        info="Accounts ranked by idle licenses (no tracked activity in the last 28 days). Every idle seat weakens the renewal — get them onboarded, or the customer right-sizes downward. The ≈ amount is the idle share of the account's 12-month billing: what those unused seats cost the customer, and what a downgrade would take off our book. Names open the account's Activity tab."
      >
        <div className="ml-auto w-44">
          <Input
            type="search"
            size="xs"
            placeholder="Search customers…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
          {formatNumber(totalIdle)} idle licenses
          {totalIdleValue > 0 && (
            <span title="Idle share of the accounts' 12-month billing — an estimate of the money paying for unused seats">
              {" · ≈ "}
              {formatCompactAmount(totalIdleValue, currency)}
            </span>
          )}
        </span>
      </PortfolioSectionHeader>

      <UsageReportCard className="flex-1">
        {targets.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            Every licensed seat in scope was active in the last 28 days.
          </p>
        ) : matching.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No customers match the search.
          </p>
        ) : (
          // Inline scroll: "Show all" must not stretch the whole page.
          <ul className="flex max-h-72 flex-col gap-2.5 overflow-y-auto pr-1">
            <li className="usage-report-list-head gap-2" aria-hidden>
              <span className="min-w-0 flex-1">Customer</span>
              <span className="w-28 text-center">Churn risk</span>
              <span className="usage-report-wide-only w-28 text-center">
                In use
              </span>
              <span className="w-16 text-right">Active / seats</span>
              <span className="w-16 text-right">Idle</span>
              <span className="w-20 text-right">≈ Idle cost</span>
            </li>
            {visible.map((target) => (
              <li
                key={target.customer.externalCustomerId}
                className="flex items-center gap-2"
              >
                <Link
                  to={buildCustomerActivityLink(
                    target.customer.externalCustomerId,
                  )}
                  iconSrc={null}
                  className="min-w-0 flex-1 truncate font-medium"
                >
                  {target.customer.name}
                </Link>
                <UsageChurnRiskBadge
                  level={target.customer.summary.churnRisk.level}
                  reasons={target.customer.summary.churnRisk.reasons}
                  className="w-28 shrink-0 justify-center"
                />
                <span
                  className="usage-report-wide-only h-2.5 w-28 shrink-0 overflow-hidden rounded-sm bg-muted/40"
                  title={`${formatNumber(target.activeSeats)} active · ${formatNumber(target.idleSeats)} idle`}
                >
                  <span
                    className="block h-full"
                    style={{
                      width: `${Math.round(target.utilization * 100)}%`,
                      backgroundColor: DATA_VIZ.brand.green2,
                    }}
                  />
                </span>
                <span className="w-16 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                  {formatNumber(target.activeSeats)}/
                  {formatNumber(target.customer.summary.seatCount)}
                </span>
                <span className="w-16 shrink-0 text-right text-xs font-semibold tabular-nums text-amber-500">
                  {formatNumber(target.idleSeats)} idle
                </span>
                <span
                  className="w-20 shrink-0 text-right text-xs tabular-nums text-muted-foreground"
                  title="Idle share of this account's 12-month billing — what the unused seats cost the customer"
                >
                  {target.idleValue12M != null && target.idleValue12M > 0
                    ? `≈ ${formatCompactAmount(
                        target.idleValue12M,
                        target.customer.primaryCurrency,
                      )}`
                    : ""}
                </span>
              </li>
            ))}
          </ul>
        )}

        {searchTerm === "" &&
          (hiddenCount > 0 || showAll) &&
          targets.length > 0 && (
            <button
              type="button"
              className="self-start text-xs text-muted-foreground underline-offset-2 hover:underline"
              onClick={() => setShowAll(!showAll)}
            >
              {showAll
                ? "Show fewer"
                : `Show all ${targets.length} (+${hiddenCount})`}
            </button>
          )}
      </UsageReportCard>
    </section>
  )
}

export default PortfolioIdleLicenses
